import { LOCALSTORAGE_KEYS, SAVE_VERSION } from '../config/constants';
import type { GameState } from '../models/GameState';
import { storage } from '../utils/storage';

/**
 * Save/Load for GameState (spec section 25).
 *
 * MVP persistence is LocalStorage. Because GameState is pure JSON data, the
 * same serialized blob can later be sent to / stored by a backend without
 * changing this contract. `saveVersion` is embedded to support migrations.
 */
export const SaveManager = {
  hasSave(): boolean {
    return storage.get(LOCALSTORAGE_KEYS.SAVE) !== null;
  },

  save(state: GameState): boolean {
    const toStore: GameState = {
      ...state,
      saveVersion: SAVE_VERSION,
      savedAt: Date.now(),
    };
    return storage.setJSON(LOCALSTORAGE_KEYS.SAVE, toStore);
  },

  load(): GameState | null {
    const data = storage.getJSON<GameState>(LOCALSTORAGE_KEYS.SAVE);
    if (data === null) return null;
    return SaveManager.migrate(data);
  },

  delete(): void {
    storage.remove(LOCALSTORAGE_KEYS.SAVE);
  },

  /**
   * Migrate an older save to the current version. In Phase 1 there is only
   * one version, so this is a passthrough — but the hook exists so future
   * schema changes have a home instead of breaking old saves.
   */
  migrate(state: GameState): GameState {
    if (state.saveVersion === SAVE_VERSION) return state;
    // Placeholder for future step-by-step migrations.
    return { ...state, saveVersion: SAVE_VERSION };
  },
};
